import React, { useState } from 'react';
import { X, Calendar, Plus, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const AddSubscriptionModal = ({ client, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Dates par défaut : aujourd'hui et dans 1 an
  const today = new Date();
  const oneYearLater = new Date(today);
  oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
  
  const [formData, setFormData] = useState({
    start_date: today.toISOString().split('T')[0],
    end_date: oneYearLater.toISOString().split('T')[0],
    status: 'active',
    payment_method: 'manual',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Convertir les dates au format ISO avec timezone
      const startDate = new Date(formData.start_date + 'T00:00:00Z').toISOString();
      const endDate = new Date(formData.end_date + 'T23:59:59Z').toISOString();
      
      await api.post(`/admin/user/${client.id}/subscriptions`, {
        user_id: client.id,
        start_date: startDate,
        end_date: endDate,
        status: formData.status,
        payment_method: formData.payment_method,
        notes: formData.notes || 'Abonnement créé manuellement par admin'
      });
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création de l\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#1A1A1D] rounded-lg metallic-border max-w-lg w-full p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center space-x-2">
            <Plus className="h-5 w-5 text-[#00FF88]" />
            <span>Ajouter un abonnement</span>
          </h3>
          <button
            onClick={onClose}
            className="text-[#6B6B6E] hover:text-white"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-[#222226] p-4 rounded-lg mb-6 border border-[#3A3A3D]">
          <div className="text-white font-semibold">{client.full_name}</div>
          <div className="text-[#B8B8BA] text-sm">{client.email}</div>
        </div>

        {success && (
          <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 mb-4 flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-[#00FF88]" />
            <span className="text-[#00FF88]">Abonnement créé avec succès !</span>
          </div>
        )}

        {error && (
          <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 mb-4 flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-[#FF3B5C]" />
            <span className="text-[#FF3B5C]">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[#B8B8BA] text-sm mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date de début
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FF88] focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-[#B8B8BA] text-sm mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date de fin
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FF88] focus:outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[#B8B8BA] text-sm mb-2">Statut</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FF88] focus:outline-none"
            >
              <option value="active">Actif</option>
              <option value="pending">En attente</option>
              <option value="suspended">Suspendu</option>
              <option value="expired">Expiré</option>
            </select>
          </div>

          <div>
            <label className="block text-[#B8B8BA] text-sm mb-2">Méthode de paiement</label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FF88] focus:outline-none"
            >
              <option value="manual">Manuel (Admin)</option>
              <option value="paypal_account">PayPal</option>
              <option value="card">Carte bancaire</option>
            </select>
          </div>

          <div>
            <label className="block text-[#B8B8BA] text-sm mb-2">Notes (optionnel)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Ex: Client existant migré depuis ancien système"
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FF88] focus:outline-none resize-none"
              rows="3"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={loading || success}
              className="flex-1 bg-[#00FF88] hover:bg-[#00E57A] text-black font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Création...</span>
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  <span>Créer l'abonnement</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 bg-[#3A3A3D] hover:bg-[#4A4A4D] text-white rounded-lg transition-all disabled:opacity-50"
            >
              Annuler
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg">
          <p className="text-[#FFB800] text-xs">
            💡 <strong>Astuce :</strong> Pour activer l'abonnement, créez ensuite des identifiants IPTV depuis la section "Validations en attente".
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AddSubscriptionModal;
